<template>
  <v-app class="pt-8">
    <v-main class="pt-8 px-6 pb-6 bt-audit">
      <v-row align="center" justify="space-between" class="pt-8 mb-2">
        <v-col cols="auto">
          <h2>Branch Transfer Audit</h2>
          <div v-if="latest" class="text-body-2 grey--text">
            Last run: {{ formatDateTime(latest.runAt) }} ({{ latest.triggerSource }}) · {{ formatDuration(latest.durationMs) }}
          </div>
        </v-col>
        <v-col cols="auto">
          <v-btn color="primary" :loading="running" @click="runReconcile()">
            <v-icon left>mdi-sync</v-icon> Run Now
          </v-btn>
        </v-col>
      </v-row>

      <v-alert v-if="latest && latest.errorMessage" type="error" dense outlined>
        Last run errored: {{ latest.errorMessage }}
      </v-alert>
      <v-alert v-if="latest && latest.healSkipped" type="warning" dense outlined>
        Stale auto-heal was skipped (stale count over the safety limit) — review before healing.
      </v-alert>

      <!-- Summary tiles -->
      <v-row v-if="latest" class="mb-4">
        <v-col v-for="tile in tiles" :key="tile.label" cols="6" sm="4" md="2">
          <v-card outlined class="text-center pa-4">
            <div class="text-h4" :class="tile.color ? tile.color + '--text' : ''">
              <v-icon v-if="tile.icon" :color="tile.color || undefined" large class="mb-1">{{ tile.icon }}</v-icon>{{ tile.value }}
            </div>
            <div class="text-subtitle-1">{{ tile.label }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

      <!-- Discrepancy lists -->
      <v-expansion-panels v-if="latest" multiple class="mb-6">
        <v-expansion-panel>
          <v-expansion-panel-header>Missing — in Cin7, not in DB ({{ latest.missingIds.length }})</v-expansion-panel-header>
          <v-expansion-panel-content>{{ idList(latest.missingIds) }}</v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>Stale — Cin7 newer than DB ({{ latest.staleIds.length }})</v-expansion-panel-header>
          <v-expansion-panel-content>{{ idList(latest.staleIds) }}</v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>Healed — re-synced this run ({{ latest.healedIds.length }})</v-expansion-panel-header>
          <v-expansion-panel-content>{{ idList(latest.healedIds) }}</v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>Orphaned — in DB, not in Cin7 ({{ latest.orphanedIds.length }}) · review &amp; delete manually</v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table>
              <thead><tr><th class="text-left">Branch Transfer Id</th><th class="text-right">Action</th></tr></thead>
              <tbody>
                <tr v-for="id in latest.orphanedIds" :key="id">
                  <td>{{ id }}</td>
                  <td class="text-right">
                    <v-btn small color="error" text :loading="deletingId === id" @click="deleteOrphan(id)">
                      <v-icon small left>mdi-delete</v-icon> Delete
                    </v-btn>
                  </td>
                </tr>
                <tr v-if="!latest.orphanedIds.length"><td colspan="2" class="grey--text">No orphans 🎉</td></tr>
              </tbody>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Run history -->
      <h3 class="mb-2">Recent Runs</h3>
      <v-data-table
        :headers="historyHeaders"
        :items="history"
        :loading="loading"
        class="elevation-1"
        :items-per-page="10">
        <template v-slot:[`item.Run_At`]="{ item }">{{ formatDateTime(item.Run_At) }}</template>
        <template v-slot:[`item.Duration_Ms`]="{ item }">{{ formatDuration(item.Duration_Ms) }}</template>
      </v-data-table>

      <v-snackbar v-model="snackActive" :color="snackColor" :timeout="snackTimeout">{{ snackText }}</v-snackbar>
    </v-main>
  </v-app>
</template>

<script>
const STOCKMARKET_API = 'https://0uxy8dccoi.execute-api.us-east-2.amazonaws.com/prod'

export default {
  name: 'BranchTransferAudit',
  data() {
    return {
      latest: null,
      history: [],
      loading: false,
      running: false,
      deletingId: null,
      snackText: '',
      snackColor: 'success',
      snackTimeout: 3000,
      snackActive: false,
      historyHeaders: [
        { text: 'Run At', value: 'Run_At' },
        { text: 'Trigger', value: 'Trigger_Source' },
        { text: 'Missing', value: 'Missing_Count' },
        { text: 'Stale', value: 'Stale_Count' },
        { text: 'Orphaned', value: 'Orphaned_Count' },
        { text: 'Healed', value: 'Healed_Count' },
        { text: 'Duration', value: 'Duration_Ms' },
      ],
    }
  },
  computed: {
    tiles() {
      if (!this.latest) return []
      const c = this.latest.cin7Total
      const d = this.latest.dbTotal
      // Compare the two totals: equal -> green "="; otherwise higher = blue up-arrow, lower = grey down-arrow.
      let cin7Cmp, dbCmp
      if (c === d) {
        cin7Cmp = { color: 'green', icon: 'mdi-equal' }
        dbCmp = { color: 'green', icon: 'mdi-equal' }
      } else if (c > d) {
        cin7Cmp = { color: 'blue', icon: 'mdi-arrow-up-bold' }
        dbCmp = { color: '', icon: 'mdi-arrow-down-bold' }
      } else {
        cin7Cmp = { color: '', icon: 'mdi-arrow-down-bold' }
        dbCmp = { color: 'blue', icon: 'mdi-arrow-up-bold' }
      }
      return [
        { label: 'Cin7 Total', value: c, color: cin7Cmp.color, icon: cin7Cmp.icon },
        { label: 'DB Total', value: d, color: dbCmp.color, icon: dbCmp.icon },
        { label: 'Missing', value: this.latest.missingCount, color: this.latest.missingCount ? 'red' : 'green' },
        { label: 'Stale', value: this.latest.staleCount, color: this.latest.staleCount ? 'orange' : 'green' },
        { label: 'Orphaned', value: this.latest.orphanedCount, color: this.latest.orphanedCount ? 'orange' : 'green' },
        { label: 'Healed', value: this.latest.healedCount, color: 'blue' },
      ]
    },
  },
  created() {
    document.title = 'Sandbox - Stock Market'
    this.$store.dispatch('updateShowWaffle', true)
    this.$store.dispatch('updateNavbarContent', [
      { title: 'Home', path: '/Ops/StockMarket/Home', icon: 'mdi-home', notification: false },
      { title: 'Manage Groups', path: '/ops/stockmarket/manage', icon: 'mdi-account-group', notification: false },
      { title: 'Manage Branch Transfers', path: '/ops/stockmarket/manageBranchTransfers', icon: 'mdi-source-branch', notification: false },
      { title: 'Manage Branches', path: '/ops/stockmarket/manageBranches', icon: 'mdi-warehouse', notification: '' },
      { title: 'Branch Transfer Audit', path: '/ops/stockmarket/branchTransferAudit', icon: 'mdi-clipboard-check', notification: false },
    ])
    this.$store.dispatch('updateNavbarTitle', 'Stock Market')
    this.getAudit()
  },
  methods: {
    async getAudit() {
      try {
        this.loading = true
        const response = await fetch(
          //Lambda Function: https://us-east-2.console.aws.amazon.com/lambda/home?region=us-east-2#/functions/StockMarket_GetBranchTransferAudit?tab=code
          `${STOCKMARKET_API}/auditbranchtransfers`,
          { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        )
        const data = await response.json()
        if (!response.ok) throw (data && data.message) || response.status
        this.latest = data.latest
        this.history = data.history || []
        this.loading = false
      } catch (error) {
        this.loading = false
        this.snack(error, 'error', 4000)
      }
    },
    async runReconcile() {
      try {
        this.running = true
        const beforeId = this.latest ? this.latest.id : 0
        const response = await fetch(
          //Lambda Function: https://us-east-2.console.aws.amazon.com/lambda/home?region=us-east-2#/functions/StockMarket_ReconcileBranchTransfers?tab=code
          `${STOCKMARKET_API}/auditbranchtransfers`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source: 'dashboard' }) }
        )
        // 202 Accepted: the reconcile runs asynchronously (it can take ~40s, beyond the
        // API Gateway 29s limit), so we poll the audit endpoint for the new run instead.
        if (!response.ok && response.status !== 202) {
          let msg = response.status
          try { msg = (await response.json()).message || msg } catch (e) { /* 202 has an empty body */ }
          throw msg
        }
        this.snack('Reconciliation started — this can take ~40s…', 'info', 5000)
        await this.pollForNewRun(beforeId)
      } catch (error) {
        this.running = false
        this.snack(error, 'error', 4000)
      }
    },
    async pollForNewRun(beforeId, attempt = 0) {
      const MAX_ATTEMPTS = 30 // ~2.5 min at 5s intervals
      await new Promise(r => setTimeout(r, 5000))
      await this.getAudit()
      if (this.latest && this.latest.id !== beforeId) {
        this.running = false
        this.snack(`Reconciliation complete — healed ${this.latest.healedCount}, ${this.latest.missingCount} missing / ${this.latest.orphanedCount} orphaned`, 'success', 5000)
        return
      }
      if (attempt + 1 >= MAX_ATTEMPTS) {
        this.running = false
        this.snack('Reconciliation is taking longer than expected — refresh shortly.', 'warning', 5000)
        return
      }
      await this.pollForNewRun(beforeId, attempt + 1)
    },
    async deleteOrphan(id) {
      try {
        this.deletingId = id
        const url = new URL(`${STOCKMARKET_API}/managebranchtransfers`)
        url.searchParams.append('id', id)
        url.searchParams.append('email', this.$store.state.user.email)
        const result = await fetch(
          //Lambda Function: https://us-east-2.console.aws.amazon.com/lambda/home?region=us-east-2#/functions/StockMarket_DeleteBranchTransfer?tab=code
          url,
          { method: 'DELETE', headers: { 'Content-Type': 'application/json' } }
        )
        const { message } = await result.json()
        if (!result.ok) throw message
        this.latest.orphanedIds = this.latest.orphanedIds.filter(o => o !== id)
        this.latest.orphanedCount = this.latest.orphanedIds.length
        this.deletingId = null
        this.snack(`Deleted orphan ${id}`, 'success', 3000)
      } catch (error) {
        this.deletingId = null
        this.snack(error, 'error', 4000)
      }
    },
    idList(ids) { return ids && ids.length ? ids.join(', ') : 'None' },
    formatDateTime(v) {
      if (!v) return '—'
      const d = new Date(v)
      if (isNaN(d.getTime())) return v
      // Run_At is stored as an already-offset wall-clock; read it back in UTC so the browser doesn't re-shift it.
      return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' })
    },
    formatDuration(ms) {
      if (ms === null || ms === undefined) return '—'
      return (ms / 1000).toFixed(1) + ' s'
    },
    snack(text, color, timeout) {
      this.snackText = text
      this.snackColor = color
      this.snackTimeout = timeout
      this.snackActive = true
    },
  },
}
</script>

<!-- Non-scoped on purpose: scoped + >>> wasn't reaching Vuetify's table cells.
     Every selector is namespaced under .bt-audit (only present on this tab), so it won't leak. -->
<style>
.bt-audit { font-size: 1.05rem; }
.bt-audit .v-data-table td,
.bt-audit .v-data-table th,
.bt-audit .v-data-table-header th,
.bt-audit .v-simple-table td,
.bt-audit .v-simple-table th {
  font-size: 1.05rem !important;
  height: auto !important;
}
.bt-audit .v-expansion-panel-header { font-size: 1.05rem !important; }
.bt-audit .v-expansion-panel-content__wrap { font-size: 1.05rem; }
</style>
